import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Badge,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaFileInvoice,
  FaDownload,
  FaEnvelope,
  FaFilter,
  FaSpinner,
  FaTrash,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { generateInvoicePDF } from "../../utils/pdfGenerator";
import Spinner from "../common/Spinner";

// Redux actions and selectors
import {
  fetchAdminInvoices,
  fetchAdminInvoiceDetails,
  downloadAdminInvoicePDF,
  sendInvoiceReminder,
  deleteAdminInvoice,
  clearErrors,
  setFilters,
  setPagination,
  clearSelectedInvoice,
  selectFilteredInvoices,
  selectPaginatedInvoices,
} from "../../redux/slices/adminInvoiceSlice";

const BillingTab = () => {
  const dispatch = useDispatch();

  // Redux state
  const {
    invoices,
    selectedInvoice,
    loading,
    error,
    detailsLoading,
    detailsError,
    reminderLoading,
    filters,
    pagination,
  } = useSelector((state) => state.adminInvoices);

  // Get filtered and paginated data from selectors
  const filteredInvoices = useSelector(selectFilteredInvoices);
  const paginatedInvoices = useSelector(selectPaginatedInvoices);

  // Local state
  const [showModal, setShowModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // Fetch invoices on component mount
  useEffect(() => {
    dispatch(fetchAdminInvoices());
  }, [dispatch]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  // Handle search and filter changes
  const handleSearchChange = (searchTerm) => {
    dispatch(setFilters({ searchTerm }));
  };

  const handleStatusFilterChange = (statusFilter) => {
    dispatch(setFilters({ statusFilter }));
  };

  const handleDateFilterChange = (dateFilter) => {
    dispatch(setFilters({ dateFilter }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    dispatch(setPagination({ currentPage: page }));
  };

  const handleItemsPerPageChange = (itemsPerPage) => {
    dispatch(setPagination({ itemsPerPage, currentPage: 1 }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewInvoice = async (invoice) => {
    try {
      await dispatch(fetchAdminInvoiceDetails(invoice.id)).unwrap();
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      alert("Failed to fetch invoice details");
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      setPdfLoading(invoice.id);

      // Fetch detailed invoice data including buyer info
      const detailedInvoice = await dispatch(
        fetchAdminInvoiceDetails(invoice.id)
      ).unwrap();

      // Use the frontend PDF generator
      await generateInvoicePDF(detailedInvoice);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete?.id) return;
    try {
      await dispatch(deleteAdminInvoice(invoiceToDelete.id)).unwrap();
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      dispatch(fetchAdminInvoices());
    } catch (error) {
      console.error("Error deleting invoice:", error);
      alert("Failed to delete invoice");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleBackendDownload = async (invoice) => {
    try {
      const result = await dispatch(
        downloadAdminInvoicePDF(invoice.id)
      ).unwrap();
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Backend download failed:", error);
      alert("Failed to download invoice");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGenerateInvoice = () => {
    // Handle generate invoice logic
    console.log("Generating new invoice");
  };

  const handleSendReminder = async (invoice) => {
    try {
      await dispatch(sendInvoiceReminder(invoice.id)).unwrap();
      alert("Reminder sent successfully!");
    } catch (error) {
      console.error("Failed to send reminder:", error);
      alert("Failed to send reminder");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    dispatch(clearSelectedInvoice());
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: "success",
      pending: "warning",
      overdue: "danger",
      cancelled: "secondary",
    };
    return (
      <Badge bg={variants[status?.toLowerCase()] || "secondary"}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const variants = {
      Premium: "primary",
      Basic: "info",
      Free: "warning",
      Custom: "dark",
    };
    return <Badge bg={variants[plan] || "secondary"}>{plan}</Badge>;
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil(
    filteredInvoices.length / pagination.itemsPerPage
  );

  if (loading) {
    return (
      <Container fluid className="p-4">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner />
            <p className="mt-3">Loading invoices...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">Invoice Management</h2>
          <p className="text-muted">
            Manage invoices, track payments, and handle billing operations.
          </p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              {error}
              <Button
                variant="link"
                onClick={() => dispatch(fetchAdminInvoices())}
                className="p-0 ms-2"
              >
                Try Again
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Search Invoices</Form.Label>
            <div className="input-group">
              <Form.Control
                type="text"
                placeholder="Search by user, company, or invoice ID..."
                value={filters.searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <Button variant="outline-secondary">
                <FaFilter />
              </Button>
            </div>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status Filter</Form.Label>
            <Form.Select
              value={filters.statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Date Filter</Form.Label>
            <Form.Select
              value={filters.dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="this-month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="this-quarter">This Quarter</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Invoices Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Invoices ({filteredInvoices.length})</h5>
            <div>
              <Badge bg="success" className="me-2">
                Paid:{" "}
                {
                  invoices.filter((i) => i.status?.toLowerCase() === "paid")
                    .length
                }
              </Badge>
              <Badge bg="warning" className="me-2">
                Pending:{" "}
                {
                  invoices.filter((i) => i.status?.toLowerCase() === "pending")
                    .length
                }
              </Badge>
              <Badge bg="danger">
                Overdue:{" "}
                {
                  invoices.filter((i) => i.status?.toLowerCase() === "overdue")
                    .length
                }
              </Badge>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>User/Company</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Date(mm/dd/yyyy)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <strong>{invoice.invoice_number}</strong>
                    <br />
                    <small className="text-muted">ID: {invoice.id}</small>
                  </td>
                  <td>
                    {invoice.company_name && (
                      <>
                        <strong>{invoice.company_name}</strong>
                        <br />
                      </>
                    )}
                    <small className="text-muted">
                      {invoice.email || "N/A"}
                    </small>
                  </td>
                  <td>{getPlanBadge(invoice.plan_name)}</td>
                  <td>{getStatusBadge(invoice.status)}</td>
                  <td>{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleDownloadPDF(invoice)}
                      disabled={pdfLoading === invoice.id}
                      title="Download PDF (Frontend)"
                    >
                      {pdfLoading === invoice.id ? (
                        <FaSpinner className="fa-spin" />
                      ) : (
                        <FaDownload />
                      )}
                    </Button>

                    {invoice.status?.toLowerCase() === "pending" && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleSendReminder(invoice)}
                        disabled={reminderLoading === invoice.id}
                        title="Send Reminder"
                      >
                        {reminderLoading === invoice.id ? (
                          <FaSpinner className="fa-spin" />
                        ) : (
                          <FaEnvelope />
                        )}
                      </Button>
                    )}

                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-1"
                      onClick={() => {
                        setInvoiceToDelete(invoice);
                        setShowDeleteModal(true);
                      }}
                      title={
                        String(invoice.status || "").toLowerCase() === "paid"
                          ? "Delete paid invoice (test cleanup)"
                          : "Delete invoice"
                      }
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {paginatedInvoices.length === 0 && (
            <div className="text-center py-4">
              <FaFileInvoice size={48} className="text-muted mb-3" />
              <p className="text-muted">No invoices found</p>
            </div>
          )}
        </Card.Body>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Form.Select
                  size="sm"
                  style={{ width: "auto" }}
                  value={pagination.itemsPerPage}
                  onChange={(e) =>
                    handleItemsPerPageChange(Number(e.target.value))
                  }
                  className="me-2"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </Form.Select>
                <small className="text-muted">
                  Showing{" "}
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    pagination.currentPage * pagination.itemsPerPage,
                    filteredInvoices.length
                  )}{" "}
                  of {filteredInvoices.length} invoices
                </small>
              </div>

              <div className="d-flex">
                {[...Array(totalPages)].map((_, index) => (
                  <Button
                    key={index + 1}
                    variant={
                      pagination.currentPage === index + 1
                        ? "primary"
                        : "outline-primary"
                    }
                    size="sm"
                    className="me-1"
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </Button>
                ))}
              </div>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Delete Invoice Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invoiceToDelete ? (
            <>
              <p className="mb-2">
                You are about to delete invoice{" "}
                <strong>{invoiceToDelete.invoice_number}</strong> (ID:{" "}
                {invoiceToDelete.id}).
              </p>
              <div className="alert alert-warning mb-0">
                This is meant for test cleanup. This will permanently delete the invoice record.
              </div>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteInvoice}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Invoice Details - {selectedInvoice?.invoice_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailsLoading ? (
            <div className="text-center py-4">
              <Spinner />
              <p className="mt-3">Loading invoice details...</p>
            </div>
          ) : detailsError ? (
            <Alert variant="danger">{detailsError}</Alert>
          ) : selectedInvoice ? (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Invoice Information</h6>
                  <p>
                    <strong>Invoice ID:</strong>{" "}
                    {selectedInvoice.invoice_number}
                  </p>
                  <p>
                    <strong>User:</strong>{" "}
                    {selectedInvoice.buyerInfo?.email || "N/A"}
                  </p>
                  <p>
                    <strong>Company:</strong>{" "}
                    {selectedInvoice.buyerInfo?.company_name || "N/A"}
                  </p>
                  <p>
                    <strong>Plan:</strong>{" "}
                    {getPlanBadge(selectedInvoice.plan_name)}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>Payment Information</h6>
                  <p>
                    <strong>Amount:</strong> $
                    {parseFloat(selectedInvoice.amount || 0).toFixed(2)}
                  </p>
                  <p>
                    <strong>Tax:</strong> $
                    {parseFloat(selectedInvoice.tax_amount || 0).toFixed(2)}
                  </p>
                  <p>
                    <strong>Total:</strong> $
                    {parseFloat(selectedInvoice.total_amount || 0).toFixed(2)}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedInvoice.status)}
                  </p>
                  <p>
                    <strong>Date:</strong>{" "}
                    {new Date(
                      selectedInvoice.invoice_date
                    ).toLocaleDateString()}
                  </p>
                </Col>
              </Row>

              {selectedInvoice.buyerInfo && (
                <Row>
                  <Col>
                    <h6>Billing Address</h6>
                    {selectedInvoice.buyerInfo.billing_name && (
                      <p>
                        <strong>Billing Name:</strong> {selectedInvoice.buyerInfo.billing_name}
                        <br />
                      </p>
                    )}
                    <p>
                      {selectedInvoice.buyerInfo.billing_address && (
                        <>
                          {selectedInvoice.buyerInfo.billing_address}
                          <br />
                        </>
                      )}
                      {selectedInvoice.buyerInfo.city &&
                        selectedInvoice.buyerInfo.state && (
                          <>
                            {selectedInvoice.buyerInfo.city},{" "}
                            {selectedInvoice.buyerInfo.state}{" "}
                            {selectedInvoice.buyerInfo.zip_code}
                            <br />
                          </>
                        )}
                      {selectedInvoice.buyerInfo.country &&
                        selectedInvoice.buyerInfo.country}
                    </p>
                    {selectedInvoice.buyerInfo.gst_number && (
                      <p>
                        <strong>GST:</strong>{" "}
                        {selectedInvoice.buyerInfo.gst_number}
                      </p>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          {selectedInvoice && (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => handleDownloadPDF(selectedInvoice)}
                disabled={pdfLoading === selectedInvoice.id}
              >
                {pdfLoading === selectedInvoice.id ? (
                  <FaSpinner className="fa-spin me-2" />
                ) : (
                  <FaDownload className="me-2" />
                )}
                Download PDF
              </Button>
              <Button
                variant="outline-warning"
                onClick={() => handleSendReminder(selectedInvoice)}
                disabled={reminderLoading === selectedInvoice.id}
              >
                {reminderLoading === selectedInvoice.id ? (
                  <FaSpinner className="fa-spin me-2" />
                ) : (
                  <FaEnvelope className="me-2" />
                )}
                Send Email
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BillingTab;
